import { teamStorage } from "../../utils/teamStorage";

/**
 * localStorage key written by logout() immediately before clearing storage.
 * Other tabs listen for the resulting `storage` event and end their own
 * sessions, ensuring a logout in one tab propagates to all open tabs.
 */
export const AUTH_LOGOUT_BROADCAST_KEY = "auth_logout_broadcast";

/**
 * Singleton in-flight refresh promise.
 *
 * Prevents concurrent token refresh calls caused by rapid route changes
 * or React StrictMode double-invocation. PingFed uses single-use refresh
 * token rotation — two simultaneous calls with the same token causes the
 * second to receive "invalid_grant". All callers share one promise.
 */
let refreshPromise: Promise<boolean> | null = null;

/** Shape of the runtime environment configuration injected via window.envConfig */
interface EnvConfig {
  clientId: string;
  authUrl: string;
  tokenUrl: string;
  revokeUrl: string;
  userInfoUrl: string;
  jwksUrl: string;
  endSessionUrl: string;
}

/**
 * Retrieves and validates the OAuth configuration from window.envConfig.
 * Throws if the config has not been loaded (e.g. env-config.js not executed).
 */
function getEnvConfig(): EnvConfig {
  const envConfig = (globalThis as typeof globalThis & { envConfig?: EnvConfig }).envConfig;
  if (!envConfig) {
    throw new Error("Environment config not loaded. Check window.envConfig");
  }
  return envConfig;
}

/**
 * Extracts the `sub` claim from a JWT without signature verification.
 * Used as a fallback when the token response body omits the `sub` field.
 *
 * @param jwt - Raw JWT string (header.payload.signature)
 * @returns The `sub` claim value, or undefined if extraction fails
 */
function extractSubFromJwt(jwt?: string): string | undefined {
  if (!jwt || typeof jwt !== "string") return undefined;
  const parts = jwt.split(".");
  if (parts.length !== 3) return undefined;

  let b64 = parts[1].replaceAll('-', '+').replaceAll('_', '/');
  const pad = b64.length % 4;
  if (pad) b64 += "=".repeat(4 - pad);

  try {
    const payload = JSON.parse(atob(b64)) as Record<string, unknown>;
    return payload?.sub as string | undefined;
  } catch {
    return undefined;
  }
}

/**
 * Checks whether a JWT access token is expired by inspecting the `exp` claim.
 *
 * @param token - Raw JWT string
 * @returns true if the token is expired or cannot be decoded
 */
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1])
    ) as Record<string, unknown>;
    if (!payload?.exp) return true;
    return Date.now() >= (payload.exp as number) * 1000;
  } catch {
    return true;
  }
}

/**
 * Generates a PKCE code verifier (43 URL-safe characters) using
 * cryptographically secure random values.
 */
function generateCodeVerifier(): string {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const random = new Uint8Array(43);
  crypto.getRandomValues(random);
  return Array.from(random)
    .map((b) => chars[b % chars.length])
    .join("");
}

/**
 * Derives a PKCE code challenge from the verifier using SHA-256 + base64url encoding.
 *
 * @param codeVerifier - The previously generated code verifier
 * @returns Base64url-encoded SHA-256 hash of the verifier
 */
async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const hash = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(codeVerifier)
  );
  const bytes = new Uint8Array(hash);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCodePoint(bytes[i]);
  }
    return btoa(binary)
      .replaceAll('+', '-')
      .replaceAll('/', '_')
      .replaceAll('=', '');
}

/**
 * Generates a cryptographically secure random state string for CSRF protection.
 * Also persists the value to sessionStorage so the callback can validate it.
 *
 * @returns 64-character hex string
 */
function generateRandomState(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const state = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  sessionStorage.setItem("oauth_state", state);
  return state;
}

export const authService = {
  /**
   * Returns the OAuth 2.0 / OpenID Connect configuration derived from
   * the runtime window.envConfig object.
   */
  getOAuthConfig: () => {
    const env = getEnvConfig();
    return {
      clientId: env.clientId,
      redirectUri: `${globalThis.location.origin}/auth/callback`,
      scope: "openid profile email",
      authUrl: env.authUrl,
      tokenUrl: env.tokenUrl,
      revokeUrl: env.revokeUrl,
      userInfoUrl: env.userInfoUrl,
      jwksUrl: env.jwksUrl,
      endSessionUrl: env.endSessionUrl,
    };
  },

  /**
   * Step 1: Initiates the OAuth 2.0 Authorization Code + PKCE login flow.
   * Generates a code verifier/challenge pair, stores them in sessionStorage,
   * and redirects the browser to the PingFederate authorization endpoint.
   */
  initiateLogin: (): void => {
    console.log("Initiating OAuth login with PKCE...");
    const config = authService.getOAuthConfig();
    const codeVerifier = generateCodeVerifier();

    generateCodeChallenge(codeVerifier).then((codeChallenge) => {
      const state = generateRandomState();
      sessionStorage.setItem("oauth_code_verifier", codeVerifier);

      const params = new URLSearchParams({
        response_type: "code",
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        scope: config.scope,
        state,
      });

      // replace() prevents the login page appearing in browser history
      globalThis.location.replace(`${config.authUrl}?${params.toString()}`);
    });
  },

  /**
   * Step 2: Exchanges the authorization code returned by PingFed for tokens.
   * Uses the PKCE code verifier previously stored in sessionStorage.
   *
   * @param code - Authorization code from the OAuth callback query parameter
   * @returns Token response including accessToken, refreshToken, idToken, and userId
   * @throws Error if the code verifier is missing or the token exchange fails
   */
  exchangeCodeForToken: async (code: string) => {
    const config = authService.getOAuthConfig();
    const codeVerifier = sessionStorage.getItem("oauth_code_verifier");
    if (!codeVerifier) {
      throw new Error("Code verifier not found in sessionStorage");
    }

    const params = new URLSearchParams({
      grant_type: "authorization_code",
      code,
      code_verifier: codeVerifier,
      redirect_uri: config.redirectUri,
      client_id: config.clientId,
    });

    const response = await fetch(config.tokenUrl, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: params.toString(),
    });

    const data = (await response.json()) as Record<string, unknown>;

    if (!response.ok) {
      throw new Error(
        `Token exchange failed: ${data.error} - ${data.error_description}`
      );
    }

    console.log("Token exchange successful");
    return {
      accessToken: data.access_token as string,
      refreshToken: data.refresh_token as string,
      idToken: data.id_token as string,
      expiresIn: data.expires_in as number,
      userId:
        (data.sub as string | undefined) ??
        extractSubFromJwt(data.id_token as string) ??
        extractSubFromJwt(data.access_token as string),
    };
  },

  /**
   * Step 3: Refreshes the access token using the stored refresh token.
   *
   * Uses a singleton promise (refreshPromise) to prevent concurrent refresh
   * calls. PingFed rotates refresh tokens on each use — two simultaneous
   * calls with the same token causes the second to fail with invalid_grant.
   *
   * All tokens are stored in sessionStorage so they are scoped to the tab
   * and cleared automatically when the browser session ends.
   *
   * @returns true if refresh succeeded and new tokens were stored, false otherwise
   */
  refreshAccessToken: async (): Promise<boolean> => {
    // Reuse an existing in-flight refresh rather than sending a second request
    if (refreshPromise) {
      console.log("Refresh already in progress — reusing existing promise");
      return refreshPromise;
    }

    refreshPromise = (async (): Promise<boolean> => {
      try {
        const config = authService.getOAuthConfig();

        // All tokens stored in sessionStorage — must read from same location
        const refreshToken = sessionStorage.getItem("refreshToken");
        if (!refreshToken) {
          console.log("No refresh token available");
          return false;
        }

        const params = new URLSearchParams({
          grant_type: "refresh_token",
          refresh_token: refreshToken,
          client_id: config.clientId,
        });

        const response = await fetch(config.tokenUrl, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params.toString(),
        });

        const data = (await response.json()) as Record<string, unknown>;
        console.log("Refresh response status:", response.status);

        if (!response.ok) {
          console.error("Token refresh failed:", data);
          // Wipe tokens so checkAndRefreshAuth falls through to Scenario 4
          sessionStorage.removeItem("accessToken");
          sessionStorage.removeItem("refreshToken");
          sessionStorage.removeItem("idToken");
          return false;
        }

        // Persist rotated tokens — always update refreshToken if server returns one
        sessionStorage.setItem("accessToken", data.access_token as string);
        if (data.refresh_token) {
          sessionStorage.setItem("refreshToken", data.refresh_token as string);
        }
        if (data.id_token) {
          sessionStorage.setItem("idToken", data.id_token as string);
        }

        console.log("Access token refreshed successfully");
        return true;
      } catch (error) {
        console.error("Token refresh error:", error);
        return false;
      } finally {
        // Release the lock so the next expiry cycle can refresh again
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  },

  /**
   * Validates the current session and refreshes the access token if expired.
   *
   * Scenarios:
   *  1. No tokens → false (must login)
   *  2. Valid access token → true
   *  3. Expired access token + refresh token → attempt refresh → true/false
   *  4. Refresh failed or no refresh token → false (must login)
   *
   * @returns true if the user has (or can obtain) a valid session
   */
  checkAndRefreshAuth: async (): Promise<boolean> => {
    // Both reads MUST use sessionStorage — tokens are written there by
    // exchangeCodeForToken callback and refreshAccessToken
    const accessToken = sessionStorage.getItem("accessToken");
    const refreshToken = sessionStorage.getItem("refreshToken");

    console.log("Checking authentication status...");
    console.log("AccessToken exists:", !!accessToken);
    console.log("RefreshToken exists:", !!refreshToken);

    if (!accessToken && !refreshToken) {
      console.log("Scenario 1: No tokens — user needs to login");
      return false;
    }

    if (accessToken && !isTokenExpired(accessToken)) {
      console.log("Scenario 2: Access token valid — user authenticated");
      return true;
    }

    if ((!accessToken || isTokenExpired(accessToken)) && refreshToken) {
      console.log("Scenario 3: Access token expired — attempting refresh");
      return authService.refreshAccessToken();
    }

    console.log("Scenario 4/5: Refresh unavailable — user needs to login");
    return false;
  },

  /** @returns Current access token from sessionStorage, or null */
  getAccessToken: (): string | null => sessionStorage.getItem("accessToken"),

  /** @returns Current refresh token from sessionStorage, or null */
  getRefreshToken: (): string | null => sessionStorage.getItem("refreshToken"),

  /** @returns Current ID token from sessionStorage, or null */
  getIdToken: (): string | null => sessionStorage.getItem("idToken"),

  /**
   * Checks whether the user currently holds a valid (non-expired) access token.
   * @returns true if a valid access token exists in sessionStorage
   */
  isAuthenticated: (): boolean => {
    const token = sessionStorage.getItem("accessToken");
    return !!token && !isTokenExpired(token);
  },

  /**
   * Performs a full logout sequence:
   *  1. Revokes access token at PingFed
   *  2. Revokes refresh token at PingFed
   *  3. Clears all storage
   *  4. Redirects to PingFed SLO endpoint to end the SSO session
   *
   * Continues through all steps even if individual revocations fail.
   */
  logout: async (): Promise<void> => {
    console.log("Logout started: revoking tokens and ending session…");

    try {
      const config = authService.getOAuthConfig();

      // Read tokens before clearing storage
      const accessToken = sessionStorage.getItem("accessToken");
      const refreshToken = sessionStorage.getItem("refreshToken");
      const idToken = sessionStorage.getItem("idToken");

      // Step 1: Revoke access token
      if (accessToken) {
        try {
          const body = new URLSearchParams({
            token: accessToken,
            client_id: config.clientId,
          });
          const res = await fetch(config.revokeUrl, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: body.toString(),
          });
          console.log("Access token revoke status:", res.status);
        } catch (e) {
          console.error("Access token revocation failed:", e);
        }
      }

      // Step 2: Revoke refresh token
      if (refreshToken) {
        try {
          const body = new URLSearchParams({
            token: refreshToken,
            client_id: config.clientId,
            token_type_hint: "refresh_token",
          });
          const res = await fetch(config.revokeUrl, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: body.toString(),
          });
          console.log("Refresh token revoke status:", res.status);
        } catch (e) {
          console.error("Refresh token revocation failed:", e);
        }
      }

      // Step 3: Clear all storage
      teamStorage.clearTeamId();
      sessionStorage.clear();
      // Broadcast logout to other tabs BEFORE clearing localStorage.
      // The `storage` event fires in all other same-origin tabs, allowing
      // them to end their own sessions immediately.
      localStorage.setItem(AUTH_LOGOUT_BROADCAST_KEY, Date.now().toString());
      localStorage.clear();
      console.log("Storage cleared");

      // Step 4: End PingFed SSO session via SLO endpoint
      const endSessionUrl = `${config.endSessionUrl}?id_token_hint=${encodeURIComponent(idToken ?? "")}`;

      // Small delay ensures revocation requests complete before redirect
      setTimeout(() => {
        globalThis.location.replace(endSessionUrl);
      }, 200);
    } catch (error) {
      console.error("Logout error:", error);
      teamStorage.clearTeamId();
      sessionStorage.clear();
      // Broadcast even in error path so other tabs still detect the logout.
      localStorage.setItem(AUTH_LOGOUT_BROADCAST_KEY, Date.now().toString());
      localStorage.clear();
      globalThis.location.replace(`${globalThis.location.origin}/login`);
    }
  },
};